import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Phone, Mail, KeyRound, Eye, Users } from "lucide-react";
import { WorkerDetailModal, STATUS_UI, fmtGrace } from "@/components/WorkersManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-lg border border-line bg-slate-100/70 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blaze";
const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500";

export const Movers = () => {
  const [movers, setMovers] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", password: "", pay_rate: "27" });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => {
    axios.get(`${API}/movers`).then(({ data }) => {
      const sorted = [...data].sort((a, b) => {
        const ca = a.contractor_name || "", cb = b.contractor_name || "";
        if (ca !== cb) return ca.localeCompare(cb);
        return a.name.localeCompare(b.name);
      });
      setMovers(sorted);
      setEdits(Object.fromEntries(sorted.map((m) => [m.id, { name: m.name, email: m.email, phone: m.phone || "", pay_rate: String(m.pay_rate), password: "" }])));
    }).catch(() => toast.error("Could not load movers."));
  };

  const setRole = async (m, role) => {
    setBusy(m.id);
    try {
      await axios.put(`${API}/workers/${m.id}/role`, { worker_role: role });
      toast.success(`${m.name} is now a ${role === "driver" ? "Driver" : "Helper"}.`);
      load();
    } catch {
      toast.error("Could not change role.");
    } finally {
      setBusy("");
    }
  };

  const grantGrace = async (m, days) => {
    setBusy(m.id);
    try {
      if (days === "revoke") {
        await axios.post(`${API}/workers/${m.id}/grace`, { revoke: true });
        toast.success(`Temporary access revoked for ${m.name}.`);
      } else {
        await axios.post(`${API}/workers/${m.id}/grace`, { days: Number(days) });
        toast.success(`${m.name} granted access for ${days === "30" ? "1 month" : `${days} days`}.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update access.");
    } finally {
      setBusy("");
    }
  };

  useEffect(load, []);

  const setEdit = (id, field, value) => setEdits((e) => ({ ...e, [id]: { ...e[id], [field]: value } }));

  const isDirty = (m) => {
    const e = edits[m.id];
    if (!e) return false;
    return e.name !== m.name || e.email !== m.email || e.phone !== (m.phone || "") || String(e.pay_rate) !== String(m.pay_rate) || e.password.length > 0;
  };

  const addMover = async () => {
    const rate = parseFloat(addForm.pay_rate);
    if (!addForm.name.trim() || !addForm.email.trim() || addForm.password.length < 6) {
      return toast.error("Enter a name, email and a password of 6+ characters.");
    }
    setAdding(true);
    try {
      await axios.post(`${API}/movers`, {
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        phone: addForm.phone.trim(),
        password: addForm.password,
        pay_rate: Number.isFinite(rate) ? rate : 27,
      });
      toast.success(`${addForm.name.trim()} added — they can log in at /mover with the password you set.`);
      setAddForm({ name: "", email: "", phone: "", password: "", pay_rate: "27" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add mover.");
    } finally {
      setAdding(false);
    }
  };

  const removeMover = async (m) => {
    if (!window.confirm(`Move ${m.name}'s account to the Bin? They won't be able to log in. You can restore them anytime from the Bin tab.`)) return;
    try {
      await axios.delete(`${API}/movers/${m.id}`);
      toast.success(`${m.name} moved to Bin — restore anytime from the Bin tab.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove mover.");
    }
  };

  const save = async (m) => {
    const e = edits[m.id];
    const rate = parseFloat(e.pay_rate);
    if (!e.name.trim() || !e.email.trim()) return toast.error("Name and email are required.");
    if (!Number.isFinite(rate) || rate < 0) return toast.error("Enter a valid rate.");
    if (e.password && e.password.length < 6) return toast.error("New password must be 6+ characters.");
    setSaving(m.id);
    try {
      const payload = { name: e.name.trim(), email: e.email.trim(), phone: e.phone.trim(), pay_rate: rate };
      if (e.password) payload.password = e.password;
      await axios.put(`${API}/movers/${m.id}`, payload);
      toast.success(`${e.name.trim()}'s details updated.${e.password ? " New password set." : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving("");
    }
  };

  const contractors = [...new Set(movers.map((m) => m.contractor_name).filter(Boolean))].sort();
  const shown = filter === "all" ? movers : movers.filter((m) => m.contractor_name === filter);
  const groups = [];
  shown.forEach((m) => {
    const key = m.contractor_name || "MoversNearby (direct)";
    let g = groups.find((x) => x.name === key);
    if (!g) { g = { name: key, items: [] }; groups.push(g); }
    g.items.push(m);
  });

  return (
    <div className="mt-8" data-testid="movers-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight text-slate-900">Movers</h2>
          <p className="mt-1 text-sm text-slate-500">Everything per mover in one place — details, pay rate, onboarding status, role, temporary access and their signed agreement.</p>
        </div>
        <button
          onClick={() => setShowAdd((s) => !s)}
          className="rounded-lg bg-blaze px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white active:scale-95"
          data-testid="add-mover-btn"
        >
          {showAdd ? "Close" : "+ Add Mover"}
        </button>
      </div>

      {contractors.length > 0 && (
        <div className="mt-5">
          <label className={labelCls}>Contractor</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full max-w-xs rounded-lg border border-line bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-blaze sm:w-auto"
            data-testid="movers-contractor-filter"
          >
            <option value="all">All contractors</option>
            {contractors.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      )}

      {showAdd && (
        <div className="mt-6 rounded-2xl border border-line bg-white p-5" data-testid="add-mover-form">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-blaze">New Mover Account</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className={labelCls}>Name</label>
              <input className={inputCls} value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="e.g. Hardeep" data-testid="add-mover-name" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" className={inputCls} value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="mover@email.com" data-testid="add-mover-email" />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input type="tel" className={inputCls} value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} placeholder="04xx xxx xxx" data-testid="add-mover-phone" />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <input className={inputCls} value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} placeholder="min 6 characters" data-testid="add-mover-password" />
            </div>
            <div>
              <label className={labelCls}>Pay Rate ($/hr)</label>
              <input type="number" min="0" step="1" className={inputCls} value={addForm.pay_rate} onChange={(e) => setAddForm({ ...addForm, pay_rate: e.target.value })} data-testid="add-mover-rate" />
            </div>
          </div>
          <button
            onClick={addMover}
            disabled={adding}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blaze px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
            data-testid="add-mover-save"
          >
            {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Create Mover Account
          </button>
        </div>
      )}

      <div className="mt-6 space-y-7" data-testid="movers-cards">
        {groups.map((g) => (
          <div key={g.name} data-testid={`movers-group-${g.name}`}>
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-blaze" />
              <p className="font-display text-sm font-bold uppercase tracking-wider text-slate-900">{g.name}</p>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{g.items.length}</span>
            </div>
            <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
        {g.items.map((m) => {
          const e = edits[m.id] || { name: m.name, email: m.email, phone: m.phone || "", pay_rate: String(m.pay_rate), password: "" };
          return (
            <div key={m.id} className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md" data-testid={`mover-row-${m.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {m.has_photo ? (
                    <img
                      src={`${API}/onboarding/photo/${m.id}`}
                      alt={m.name}
                      className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-sm ring-2 ring-blue-500/20"
                      data-testid={`mover-photo-${m.name.toLowerCase()}`}
                    />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blaze font-display text-lg font-bold text-white shadow-sm ring-2 ring-blue-500/20">
                      {m.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="font-display text-base font-bold text-slate-900">{m.name}</p>
                    {m.worker_role && (
                      <p className="text-[10px] font-bold uppercase tracking-widest text-blaze" data-testid={`mover-role-${m.name.toLowerCase()}`}>
                        {m.worker_role === "driver" ? "Driver" : "Helper / Offsider"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  {(() => { const s = STATUS_UI[m.status] || STATUS_UI.locked; return (
                    <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${s.cls}`} data-testid={`worker-status-${m.name.toLowerCase()}`}>
                      <s.Icon className="h-3 w-3" /> {s.label}
                    </span>
                  ); })()}
                  <span className="rounded-full bg-green-100 px-3 py-1 text-[10px] font-bold text-green-700">${m.pay_rate}/hr</span>
                </div>
              </div>
              {m.status === "temporary" && m.grace_until && (
                <p className="mt-2 rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-700" data-testid={`grace-until-${m.name.toLowerCase()}`}>
                  Access expires {fmtGrace(m.grace_until)}
                </p>
              )}
              {m.agreement_signed && (
                <p className="mt-2 text-[11px] font-semibold text-green-700">Agreement signed {m.signed_date}</p>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <select
                  value={m.worker_role || ""}
                  onChange={(ev) => ev.target.value && setRole(m, ev.target.value)}
                  disabled={busy === m.id}
                  className="rounded-lg border border-line bg-slate-50 px-2 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-blaze"
                  data-testid={`role-select-${m.name.toLowerCase()}`}
                >
                  <option value="" disabled>Set role…</option>
                  <option value="driver">Driver</option>
                  <option value="helper">Helper</option>
                </select>
                <select
                  value=""
                  onChange={(ev) => ev.target.value && grantGrace(m, ev.target.value)}
                  disabled={busy === m.id || m.status === "active"}
                  className="rounded-lg border border-line bg-slate-50 px-2 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-blaze disabled:opacity-40"
                  data-testid={`grace-select-${m.name.toLowerCase()}`}
                >
                  <option value="" disabled>Grant access…</option>
                  <option value="2">Grant Access: 2 Days</option>
                  <option value="4">Grant Access: 4 Days</option>
                  <option value="30">Grant Access: 1 Month</option>
                  {m.status === "temporary" && <option value="revoke">Revoke Access</option>}
                </select>
              </div>
              <button
                onClick={() => setDetail(m.id)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-blaze py-2 text-[11px] font-bold uppercase tracking-wider text-blaze active:scale-95"
                data-testid={`worker-view-${m.name.toLowerCase()}`}
              >
                <Eye className="h-3.5 w-3.5" /> View Profile &amp; Agreement
              </button>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Name</label>
                  <input className={inputCls} value={e.name} onChange={(ev) => setEdit(m.id, "name", ev.target.value)} data-testid={`edit-name-${m.name.toLowerCase()}`} />
                </div>
                <div>
                  <label className={labelCls}>Pay Rate ($/hr)</label>
                  <input type="number" min="0" step="1" className={inputCls} value={e.pay_rate} onChange={(ev) => setEdit(m.id, "pay_rate", ev.target.value)} data-testid={`rate-input-${m.name.toLowerCase()}`} />
                  <p className="mt-1 text-[10px] leading-snug text-slate-400">Rate change applies from today — earlier jobs keep the old rate.</p>
                  {(m.rate_history || []).length > 1 && (
                    <p className="mt-0.5 text-[10px] font-semibold text-slate-500" data-testid={`rate-history-${m.name.toLowerCase()}`}>
                      History: {m.rate_history.map((h) => `$${h.rate}${h.effective_from ? ` from ${h.effective_from}` : " (initial)"}`).join(" → ")}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className={labelCls}><Mail className="mr-1 inline h-3 w-3" />Email</label>
                  <input type="email" className={inputCls} value={e.email} onChange={(ev) => setEdit(m.id, "email", ev.target.value)} data-testid={`edit-email-${m.name.toLowerCase()}`} />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}><Phone className="mr-1 inline h-3 w-3" />Phone</label>
                  <input type="tel" className={inputCls} value={e.phone} onChange={(ev) => setEdit(m.id, "phone", ev.target.value)} placeholder="04xx xxx xxx" data-testid={`edit-phone-${m.name.toLowerCase()}`} />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}><KeyRound className="mr-1 inline h-3 w-3" />New Password (optional)</label>
                  <input className={inputCls} value={e.password} onChange={(ev) => setEdit(m.id, "password", ev.target.value)} placeholder="leave blank to keep current" data-testid={`edit-password-${m.name.toLowerCase()}`} />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => save(m)}
                  disabled={saving === m.id || !isDirty(m)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blaze py-2.5 text-xs font-bold uppercase tracking-widest text-white active:scale-95 disabled:opacity-40"
                  data-testid={`rate-save-${m.name.toLowerCase()}`}
                >
                  {saving === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </button>
                <button
                  onClick={() => removeMover(m)}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-red-300 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-red-600 transition-colors hover:bg-red-50 active:scale-95"
                  data-testid={`remove-mover-${m.name.toLowerCase()}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
            </div>
          );
        })}
            </div>
          </div>
        ))}
      </div>
      {detail && <WorkerDetailModal workerId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};
